import { useState, useEffect, useRef } from "react";
import { useNotifications } from "utils/hooks";
import { logError } from "utils/error";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../constants/ZoomAttendance.constants";
import {
  useBookedCustomersQuery,
  useMapUnrecognizedParticipantMutation,
} from "./useReconciliationQueries";

/**
 * Custom hook to manage state and async handlers for the ReconciliationModal component.
 *
 * @param {Object} params - Hook parameters.
 * @param {boolean} params.open - Whether the modal is visible.
 * @param {string} params.slotUuid - Slot UUID for the session.
 * @param {string} params.listingUuid - Listing UUID for the session.
 * @param {Object} params.participant - Selected participant object.
 * @param {Function} params.onReconcileSuccess - Callback triggered after successful reconciliation.
 * @param {Function} params.onClose - Modal close handler callback.
 * @returns {Object} Search state, selection handlers, loading states, and confirmation action.
 */
export const useReconciliationModal = ({
  open,
  slotUuid,
  listingUuid,
  participant,
  onReconcileSuccess,
  onClose,
}) => {
  const { notify } = useNotifications();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBookingUuid, setSelectedBookingUuid] = useState("");
  const [selectedBookingData, setSelectedBookingData] = useState(null);
  const submitInFlightRef = useRef(false);

  const {
    customers: bookings,
    isLoading: searchLoading,
    isFetching: searchFetching,
  } = useBookedCustomersQuery({
    slotUuid: open ? slotUuid : "",
    query: searchQuery,
  });

  const { mutateAsync: mapParticipant, isLoading: submitLoading } =
    useMapUnrecognizedParticipantMutation();

  useEffect(() => {
    if (open) {
      setSearchQuery(ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.EMPTY_STRING);
      setSelectedBookingUuid(ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.EMPTY_STRING);
      setSelectedBookingData(null);
    }
  }, [open]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectBooking = (customer) => {
    setSelectedBookingUuid(customer.customer_uuid);
    setSelectedBookingData(customer);
  };

  const handleConfirmMapping = async () => {
    if (submitInFlightRef.current || submitLoading) return;
    const participantEmail =
      participant?.customer_email ||
      participant?.user_email ||
      participant?.email ||
      "";

    if (!selectedBookingUuid || !participantEmail.trim()) {
      if (!participantEmail.trim()) {
        notify("Participant email is required", "error");
      }
      return;
    }

    submitInFlightRef.current = true;
    try {
      await mapParticipant({
        listing_uuid: listingUuid,
        slot_uuid: slotUuid,
        customer_uuid: selectedBookingUuid,
        participant_email: participantEmail.trim(),
      });

      notify(
        ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.RECONCILIATION_SUCCESS ||
          ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.RECONCILE_SUCCESS,
        "success"
      );
      if (onReconcileSuccess) onReconcileSuccess();
      onClose();
    } catch (err) {
      logError({
        error: err,
        when: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.CONFIRM_MAP_ERROR_WHEN,
        occuredAt:
          ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.RECONCILIATION_MODAL_OCCURRENCE,
      });
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.RECONCILE_FAILURE;
      notify(errorMsg, "error");
    } finally {
      submitInFlightRef.current = false;
    }
  };

  return {
    searchQuery,
    selectedBookingUuid,
    selectedBookingData,
    bookings,
    searchLoading,
    searchFetching: Boolean(searchFetching),
    submitLoading: Boolean(submitLoading || submitInFlightRef.current),
    handleSearchChange,
    handleSelectBooking,
    handleConfirmMapping,
  };
};
